import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";

/**
 * Horizontally scrollable card row with page dots derived from its rendered width.
 * Vertical wheel gestures become horizontal movement; drag/trackpad scrolling and
 * dot clicks all keep the active page in sync.
 */
export function HorizontalCardCarousel({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      el.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame: number | undefined;
    const update = () => {
      const width = el.clientWidth || 1;
      const maxScroll = el.scrollWidth - width;
      // A partially visible final page still needs its own dot.
      const count = maxScroll > 4 ? Math.max(1, Math.ceil(el.scrollWidth / width)) : 1;
      setPageCount(count);
      setPage(maxScroll > 0 ? Math.round((el.scrollLeft / maxScroll) * (count - 1)) : 0);
    };
    const scheduleUpdate = () => {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        update();
      });
    };

    update();
    el.addEventListener("scroll", scheduleUpdate, { passive: true });

    // The scroll container keeps its own width while its content changes: the first
    // render happens before the query resolves, so measuring only the container
    // misses every card that arrives later and the dots stay hidden until the first
    // scroll event. Therefore the cards are observed as well, and childList changes
    // re-subscribe to whatever set of cards is currently mounted.
    //
    // Ignore height-only notifications: ShareItemCardCover animates its own height on
    // hover (grid-rows 0fr -> 1fr over 500ms), which fires this observer on every frame
    // of that transition even though pagination only depends on horizontal geometry.
    // Reading scroll geometry in update() forces a layout recalc, and doing that every
    // frame competes with the hover transition (confirmed via Safari Timelines: Layout
    // and Recalculate Styles firing in lockstep with pointermove while just hovering a
    // card, not once per hover as a pure CSS transition would). Track each observed
    // target's last known width and only schedule an update when it actually changed.
    const lastWidths = new WeakMap<Element, number>();
    const resizeObserver = new ResizeObserver((entries) => {
      let widthChanged = false;
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (lastWidths.get(entry.target) !== width) {
          lastWidths.set(entry.target, width);
          widthChanged = true;
        }
      }
      if (widthChanged) scheduleUpdate();
    });
    resizeObserver.observe(el);
    const observeCards = () => {
      for (const card of Array.from(el.children)) resizeObserver.observe(card);
    };
    observeCards();
    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const removed of Array.from(record.removedNodes)) {
          if (removed instanceof Element) resizeObserver.unobserve(removed);
        }
      }
      observeCards();
      scheduleUpdate();
    });
    mutationObserver.observe(el, { childList: true });

    return () => {
      el.removeEventListener("scroll", scheduleUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div>
      <div
        ref={ref}
        className="flex items-start gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {pageCount > 1 ? (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                const el = ref.current;
                if (!el) return;
                const left =
                  pageCount > 1
                    ? (index / (pageCount - 1)) * (el.scrollWidth - el.clientWidth)
                    : 0;
                el.scrollTo({ left, behavior: "smooth" });
              }}
              aria-label={intl.formatMessage(
                { id: "home.carouselSlide", defaultMessage: "Slide {number}" },
                { number: index + 1 },
              )}
              className={`h-1.5 rounded-full transition-all ${
                index === page ? "w-5 bg-accent" : "w-1.5 bg-ink/20 hover:bg-ink/40"
              }`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
